import { Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ClientPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { NewAccountStatus } from 'constants/new_accounts.constants';
import { setNewAccount } from 'state/reducers/new_accounts.reducers';
import {
	getNewAccounts,
	getNewBankAccountByAccountId,
	getNewUsersByAccountId,
	deleteNewAccountAndCompany,
} from 'services/new_accounts.service';
import { getCustomerWithTranslatedType } from './helper';
import { getUserDetails } from 'state/reducers/user.reducers';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { useConfirmationDialog } from 'hooks/shared/useConfirmationDialog';
import { ConfirmationDialog } from 'components/ConfirmationDialog';

type IProps = {
	draftAccounts: Record<string, any>;
};

export const AccountsInProcessTable: React.FC<IProps> = ({ draftAccounts }: IProps) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [accounts, setAccounts] = useState<any>([]);
	const details = useAppSelector(getUserDetails);
	const deleteAccountPermission = hasPermission([permissionEnums.REMOVAL_ACCOUNT]);

	const { dialogConfig, openDialog, handleConfirm, handleCancel } = useConfirmationDialog();

	useEffect(() => {
		if (draftAccounts) {
			const data = getCustomerWithTranslatedType(draftAccounts, t);
			setAccounts(data);
		}
	}, [draftAccounts]);

	const handleDeleteClick = (accountId: number) => {
		const account = accounts.find((acc: any) => acc.id === accountId);
		if (!account) return;

		openDialog({
			title: t('page.newAccount.delete.confirmation.title'),
			message: t('page.newAccount.delete.confirmation.message').replace(
				'{accountName}',
				account.accountNameHebrew || account.accountNameEnglish
			),
			confirmLabel: t('actions.deleteAccount'),
			cancelLabel: t('actions.cancel'),
			onConfirm: async () => {
				try {
					await dispatch(deleteNewAccountAndCompany(accountId));
				} catch (error) {
					console.error('Failed to delete account:', error);
				}
			},
		});
	};

	const tableContent = (
		<Stack spacing={2}>
			{draftAccounts?.length > 0 && (
				<>
					<Typography variant="subtitle" color={theme.palette.root.grey}>
						{draftAccounts?.length + ' ' + t('messages.results')}
					</Typography>
					<ClientPaginatedTable
						rowAction={(row) => {
							if (row.status === NewAccountStatus.DENIED) {
								//not denied by this user
								if (details?.id !== row?.approvedByBoUserId) {
									navigate(pagesEnum.NEW_ACCOUNT + '/create/' + row.id);
								}
							} else {
								navigate(pagesEnum.NEW_ACCOUNT + '/create/' + row.id);
							}
						}}
						data={TableConfig.tableData(accounts)}
						columns={TableConfig.buildColumns(
							NewAccountStatus.DRAFT,
							t,
							deleteAccountPermission ? handleDeleteClick : undefined
						)}
					/>
				</>
			)}
		</Stack>
	);

	return (
		<>
			{tableContent}

			<ConfirmationDialog
				open={dialogConfig.open}
				title={dialogConfig.title}
				message={dialogConfig.message}
				confirmLabel={dialogConfig.confirmLabel}
				cancelLabel={dialogConfig.cancelLabel}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
				confirmColor="error"
				loading={dialogConfig.loading}
				error={dialogConfig.error}
			/>
		</>
	);
};
